import React, { useRef } from 'react'
import { useHistory } from 'react-router'
import moment from 'moment-timezone'
import { Column, Paging, Pager, LoadPanel } from 'devextreme-react/data-grid'
import { ActiveSearchModel } from 'domain/entities/active-search-model'
import {
  ActiveSearchSurgeryTranslated,
  ActiveSearchSurgeryType
} from 'common/enum/active-search-surgery-type'
import {
  ActiveSearchHelpStatus,
  ActiveSearchHelpStatusLocated
} from 'common/enum/active-search-help-status'
import { capitalizeText } from 'common/utils/capitalizeText'
import { capitalizeEachWordText } from 'common/utils/capitalizeEachWordText'
import DataGrid from 'presentation/shared/components/DataGrid'
import ButtonNew from 'presentation/shared/components/ButtonNew'
import TextFieldNew from 'presentation/shared/components/TextFieldNew'
import { TooltipNew } from 'presentation/shared/components/TooltipNew'
import { ReactComponent as PendentIcon } from 'presentation/assets/icons/pendent-icon.svg'
import { ReactComponent as SearchIcon } from 'presentation/assets/icons/search.svg'
import * as S from './styles'

type Props = {
  data: ActiveSearchModel[]
  type?: ActiveSearchSurgeryType
  // status?: ActiveSearchHelpStatus
}

const ActiveSearchDataGrid = ({ data, type }: Props) => {
  const history = useHistory()
  const dataGridRef = useRef<any>(null)
  const filtersRef = useRef<any>(null)
  const inputSearchPanelRef = useRef<any>(null)
  const allowedPageSizes = [5, 10, 15, 20, 30]

  const handleClearFilters = (e: any) => {
    e.preventDefault()
    dataGridRef.current.instance.clearFilter()
    filtersRef.current.reset()
  }

  const handleSearchPanel = (e: any) => {
    e.preventDefault()
    dataGridRef.current.instance.searchByText(inputSearchPanelRef.current.value)
  }

  const handleRowClick = (event: any) => {
    const surgicalOrderId: number = event.data.surgical_order_id
    // const surgicalOrder: SurgicalOrderModelNew = event.data.surgical_order
    // const status = surgicalOrder.active_status

    if (surgicalOrderId) {
      history.push('/pedido', {
        id: surgicalOrderId
        // status:
      })
    }
  }

  const BoxDefaultLineClampRender = (item: any) =>
    item?.value && (
      <TooltipNew
        text={item?.value}
        type="standard"
        placement="bottom-start"
        cursor="pointer"
      >
        <S.BoxDefault lineClamp>{item.value}</S.BoxDefault>
      </TooltipNew>
    )

  const BoxDefaultCenteredRender = (item: any) =>
    item?.value && <S.BoxDefaultCentered>{item.value}</S.BoxDefaultCentered>

  const ProcedureTextRender = (item: any) =>
    item?.value && (
      <TooltipNew
        text={item?.value}
        type="standard"
        placement="bottom-start"
        cursor="pointer"
      >
        <S.BoxDefault lineClamp>{item.value}</S.BoxDefault>
      </TooltipNew>
    )

  const HelpStatusRender = (item: any) => {
    return (
      item?.value && (
        <TooltipNew
          text={
            ActiveSearchHelpStatusLocated[
              item?.value as keyof typeof ActiveSearchHelpStatusLocated
            ]
          }
          type="standard"
          placement="right"
          cursor="pointer"
        >
          <S.HelpStatusBadge status={item?.value}>
            <PendentIcon className="icon" />
          </S.HelpStatusBadge>
        </TooltipNew>
      )
    )
  }

  const HelpStatusHeaderFilter = [
    {
      text: 'À Resolver',
      value: ['help_status', '=', ActiveSearchHelpStatus.PENDENT]
    },
    {
      text: 'Em Andamento',
      value: ['help_status', '=', ActiveSearchHelpStatus.IN_PROGRESS]
    },
    {
      text: 'Resolvida',
      value: ['help_status', '=', ActiveSearchHelpStatus.RESOLVED]
    }
  ]

  const formattedData = React.useMemo(() => {
    return data?.map((item) => ({
      // ...item,
      help_status: item.status,
      need_help:
        item.need_help === '' || item.need_help === null
          ? ''
          : item.need_help
          ? 'Sim'
          : 'Não',
      surgical_order_id: item.surgical_order_id,
      type:
        ActiveSearchSurgeryType[type!] === ActiveSearchSurgeryType.OTHER
          ? capitalizeText(item.observation)
          : ActiveSearchSurgeryTranslated[item.type] ?? '',
      patient_name: capitalizeEachWordText(item.surgical_order.patient.name),
      main_procedure: capitalizeText(
        String(
          item.surgical_order.procedures?.find(
            (procedure: any) => procedure.isMain
          )?.description || ''
        )
      ),
      doctor_name: capitalizeEachWordText(item.surgical_order.doctor.name),
      hospital_name: item.surgical_order.hospital.name,
      answered_at: moment(item.created_at)
        .utc()
        .format('DD/MM/YYYY [às] HH:mm[h]')
    }))
  }, [data])

  return (
    <S.Wrapper>
      <S.Filters ref={filtersRef} onSubmit={handleSearchPanel}>
        <div className="inputs">
          <TextFieldNew
            ref={inputSearchPanelRef}
            className="input-search-panel"
            placeholder="Buscar por paciente ou número do pedido"
            iconPosition="insideLeft"
            icon={<SearchIcon />}
          />
        </div>
        <div className="actions">
          <ButtonNew type="submit" size="large" onClick={handleSearchPanel}>
            Buscar
          </ButtonNew>
          <ButtonNew type="button" size="large" onClick={handleClearFilters}>
            Limpar
          </ButtonNew>
        </div>
      </S.Filters>

      <DataGrid
        ref={dataGridRef}
        dataSource={formattedData}
        onRowClick={handleRowClick}
        sorting={{
          mode: 'single' // (?)
        }}
      >
        <Column
          caption="Status da ajuda"
          dataField="help_status"
          headerFilter={{
            allowSearch: false,
            dataSource: HelpStatusHeaderFilter
          }}
          allowSorting={false}
          width="150px"
          cellRender={HelpStatusRender}
        />
        <Column
          caption="Precisa de ajuda"
          dataField="need_help"
          headerFilter={{
            allowSearch: false
          }}
          allowSearch={false}
          width="160px"
        />
        <Column
          caption="Nº aviso"
          dataField="surgical_order_id"
          allowSorting={false}
          allowFiltering={false}
          allowHeaderFiltering={false}
          allowSearch={true}
          alignment="center"
          width="105px"
          cellRender={BoxDefaultCenteredRender}
        />
        <Column
          caption="Motivo de não realização da cirurgia"
          dataField="type"
          allowSorting={false}
          allowFiltering={true}
          allowHeaderFiltering={true}
          allowSearch={false}
          headerFilter={{
            allowSearch: true,
            width: 550
          }}
          width="300px"
          cellRender={BoxDefaultLineClampRender}
        />
        <Column
          caption="Paciente"
          dataField="patient_name"
          allowFiltering={false}
          allowSearch={true}
          allowSorting={false}
          cellRender={BoxDefaultLineClampRender}
          width="200px"
        />
        <Column
          caption="Cirurgia principal"
          dataField="main_procedure"
          allowFiltering={true}
          allowSearch={false}
          allowSorting={false}
          headerFilter={{
            allowSearch: true,
            width: 450
          }}
          width="220px"
          cellRender={ProcedureTextRender}
        />
        <Column
          caption="Médico"
          dataField="doctor_name"
          allowFiltering={true}
          allowSearch={false}
          headerFilter={{
            allowSearch: true,
            width: 350
          }}
          allowSorting={false}
          width="220px"
          cellRender={BoxDefaultLineClampRender}
        />
        <Column
          caption="Unidade"
          dataField="hospital_name"
          allowFiltering={false}
          allowSorting={false}
          headerFilter={{
            allowSearch: false
          }}
          alignment="center"
        />
        <Column
          caption="Dt. de resposta"
          dataField="answered_at"
          allowFiltering={false}
          allowSorting={false}
          alignment="center"
          cellRender={BoxDefaultCenteredRender}
        />

        {/* Pagination */}
        <Paging enabled={true} defaultPageSize={20} defaultPageIndex={0} />
        <Pager
          visible={true}
          showPageSizeSelector={true}
          allowedPageSizes={allowedPageSizes}
          showNavigationButtons={true}
          showInfo={true}
          infoText="Página {0} de {1} ({2} itens)"
        />
        <LoadPanel enabled={false} />
      </DataGrid>
    </S.Wrapper>
  )
}

export default ActiveSearchDataGrid
